import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaLaptopHouse, FaCalendarDay, FaBoxOpen, FaExclamationTriangle, FaCheckCircle } from "react-icons/fa";
import api from "../../api";
import { Card, StatCard, StatCardGrid, DataTable, Button, ErrorState } from "../../components/ui";
import { dateOnlyParts, formatDateOnly, toDateOnlyString } from "../../utils/dateOnly";

// Phase 52 (New Joiner Asset Request). Technician Dashboard, task spec
// §16-18: a self-contained section using the CANONICAL portal components
// (components/ui -- StatCard/DataTable/Button, the same primitives every
// other role dashboard is built from) rather than this page's own bespoke
// `td-*` markup, so it renders with the app's standard maroon/gold portal
// theme (Button variant="primary" already resolves to --brand-maroon via
// theme/portalTokens.css) and the standard table chrome/horizontal-scroll
// behavior, with zero new CSS of its own beyond a couple of layout rules.
const STATUS_TONE = {
  SUBMITTED: "gray",
  ASSIGNED: "blue",
  IN_PREPARATION: "amber",
  READY: "purple",
  DEPLOYED: "green",
  CANCELLED: "red",
};
const STATUS_LABEL = {
  SUBMITTED: "Submitted",
  ASSIGNED: "Assigned",
  IN_PREPARATION: "In Preparation",
  READY: "Ready",
  DEPLOYED: "Deployed",
  CANCELLED: "Cancelled",
};

function StatusPillNJ({ status }) {
  const tone = STATUS_TONE[status] || "gray";
  return <span className={`ui-badge ui-badge-${tone}`}>{STATUS_LABEL[status] || status}</span>;
}

// Timezone-safe, date-only countdown (task spec §18) -- computed from the
// plain 'YYYY-MM-DD' portion of joining_date only, never from a Date
// object's own hours/timezone, so it can never drift by a day depending on
// the viewer's browser timezone.
function daysBetweenDateOnly(fromParts, toParts) {
  const fromMs = Date.UTC(fromParts.y, fromParts.mo - 1, fromParts.d);
  const toMs = Date.UTC(toParts.y, toParts.mo - 1, toParts.d);
  return Math.round((toMs - fromMs) / 86400000);
}
function todayIstParts() {
  const formatter = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit" });
  const str = formatter.format(new Date());
  const [y, mo, d] = str.split("-").map(Number);
  return { y, mo, d };
}
function countdownLabel(joiningDate) {
  const joining = dateOnlyParts(joiningDate);
  if (!joining) return { label: "-", overdue: false };
  const diff = daysBetweenDateOnly(todayIstParts(), joining);
  if (diff > 1) return { label: `Joining in ${diff} days`, overdue: false };
  if (diff === 1) return { label: "Joining Tomorrow", overdue: false };
  if (diff === 0) return { label: "Joining Today", overdue: false };
  return { label: `Overdue by ${Math.abs(diff)} days`, overdue: true };
}
const formatDate = (value) => formatDateOnly(value);

export default function NewJoinerDeploymentSection() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get("/new-joiner-requests/dashboard/summary"),
      // current=1: one row per employee -- their newest non-cancelled
      // request. An older request re-submitted with a corrected joining
      // date no longer shows (or counts) as a second, stale joiner.
      api.get("/new-joiner-requests", { params: { current: 1 } }),
    ])
      .then(([summaryRes, listRes]) => {
        setSummary(summaryRes.data?.data || null);
        setRows(Array.isArray(listRes.data?.data) ? listRes.data.data : []);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || "Failed to load New Joiner deployments.");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Sort by nearest joining date first (task spec §17); overdue (past
  // joining date, still pending) naturally sorts to the top since it's
  // furthest in the past among still-active requests.
  const sortedRows = useMemo(() => {
    return [...rows]
      .filter((r) => r.status !== "CANCELLED")
      .sort((a, b) => String(toDateOnlyString(a.joining_date) || "").localeCompare(String(toDateOnlyString(b.joining_date) || "")));
  }, [rows]);

  if (error) {
    return (
      <Card title="New Joiner Device Deployments">
        <ErrorState message={error} action={<Button onClick={load}>Retry</Button>} />
      </Card>
    );
  }

  return (
    <section style={{ marginTop: 16 }}>
      <Card
        title="New Joiner Device Deployments"
        actions={<Button variant="secondary" size="sm" onClick={load}>Refresh</Button>}
      >
        <StatCardGrid>
          <StatCard icon={<FaCalendarDay />} label="Upcoming Joiners" value={summary?.upcomingJoiners ?? 0} tone="info" loading={loading} />
          <StatCard icon={<FaExclamationTriangle />} label="Due Today" value={summary?.dueToday ?? 0} tone="warning" loading={loading} />
          <StatCard icon={<FaBoxOpen />} label="Ready" value={summary?.ready ?? 0} tone="purple" loading={loading} />
          <StatCard icon={<FaExclamationTriangle />} label="Overdue" value={summary?.overdue ?? 0} tone="danger" loading={loading} />
          <StatCard icon={<FaCheckCircle />} label="Deployed This Month" value={summary?.deployedThisMonth ?? 0} tone="success" loading={loading} />
        </StatCardGrid>

        <DataTable
          loading={loading}
          empty={!loading && sortedRows.length === 0}
          emptyIcon={FaLaptopHouse}
          emptyTitle="No New Joiner requests"
          emptyMessage="Requests submitted via the public New Joiner Asset Request form will appear here once assigned to you."
          columns={[
            { key: "request", label: "Request" },
            { key: "employee", label: "Employee" },
            { key: "company", label: "Company" },
            { key: "designation", label: "Designation" },
            { key: "joining", label: "Joining Date" },
            { key: "assets", label: "Required Assets" },
            { key: "status", label: "Status", align: "center" },
            { key: "deployment", label: "Deployment" },
            { key: "actions", label: "Actions", align: "center" },
          ]}
        >
          {sortedRows.map((row) => {
            const countdown = row.status === "DEPLOYED" ? null : countdownLabel(row.joining_date);
            return (
              <tr key={row.id}>
                <td>{row.request_number}</td>
                <td>
                  {row.employee_name}
                  {row.superseded_open_requests && (
                    <small className="d-block text-warning" title="An older request for this employee is still open. Cancel it if it is a duplicate.">
                      Older open request: {row.superseded_open_requests}
                    </small>
                  )}
                </td>
                <td>{row.company_name || "-"}</td>
                <td>{row.designation}</td>
                <td>{formatDate(row.joining_date)}</td>
                <td>{row.required_assets || "-"}</td>
                <td className="ui-th-center"><StatusPillNJ status={row.status} /></td>
                <td>
                  {row.status === "DEPLOYED" ? (
                    <span className="ui-badge ui-badge-green">Deployed {formatDate(row.deployment_date)}</span>
                  ) : (
                    <span className={countdown?.overdue ? "ui-badge ui-badge-red" : "ui-badge ui-badge-blue"}>{countdown?.label}</span>
                  )}
                </td>
                <td className="ui-th-center">
                  <Button size="sm" onClick={() => navigate(`/technician/new-joiner-requests/${row.id}`)}>View</Button>
                </td>
              </tr>
            );
          })}
        </DataTable>
      </Card>
    </section>
  );
}
